// Interactive canvas-based component
// Should implement: mousemove, mouseout, mouseup, mousedown, click

import Utils from '../stuff/utils.js'

export default {
    methods: {
        setup() {
            const canvas = this.$refs.canvas
            let dpr = window.devicePixelRatio || 1
            canvas.style.width = `${this._attrs.width}px`
            canvas.style.height = `${this._attrs.height}px`
            if (dpr < 1) dpr = 1 // Realy ? That's it? Issue #63

            // Optional second, transparent canvas stacked on top of the
            // primary one — used by components (currently just Grid) that
            // want to repaint a cheap, independent layer (e.g. the
            // crosshair) on every mousemove without re-drawing every
            // overlay on the primary canvas. Opt-in via create_canvas's
            // `secondary: true`; harmless no-op for everything else.
            const xcanvas = this._hasSecondaryCanvas ?
                this.$refs.cursorCanvas : null
            if (xcanvas) {
                xcanvas.style.width = `${this._attrs.width}px`
                xcanvas.style.height = `${this._attrs.height}px`
            }

            this.$nextTick(() => {
                var rect = canvas.getBoundingClientRect()
                canvas.width = rect.width * dpr
                canvas.height = rect.height * dpr
                const ctx = canvas.getContext('2d', {
                    // TODO: test the boost:
                    //alpha: false,
                    //desynchronized: true,
                    //preserveDrawingBuffer: false
                })
                ctx.scale(dpr, dpr)
                this.redraw()
                // Fallback fix for Brave browser
                // https://github.com/brave/brave-browser/issues/1738
                if (!ctx.measureTextOrg) {
                    ctx.measureTextOrg = ctx.measureText
                }
                ctx.measureText = text =>
                    Utils.measureText(ctx, text, this.$props.tv_id)

                if (xcanvas) {
                    const xrect = xcanvas.getBoundingClientRect()
                    xcanvas.width = xrect.width * dpr
                    xcanvas.height = xrect.height * dpr
                    const xctx = xcanvas.getContext('2d')
                    xctx.scale(dpr, dpr)
                    if (this.onSecondaryCanvas) {
                        this.onSecondaryCanvas(xcanvas, xctx)
                    }
                }
            })
        },
        create_canvas(h, id, props) {
            this._id = id
            this._attrs = props.attrs
            this._hasSecondaryCanvas = !!props.secondary
            const layers = [
                h('canvas', {
                    on: {
                        mouseup: e => this.renderer.mouseup(e),
                        mousedown: e => this.renderer.mousedown(e)
                    },
                    attrs: Object.assign({
                        id: `${this.$props.tv_id}-${id}-canvas`
                    }, props.attrs),
                    ref: 'canvas',
                    style: Object.assign({}, props.style, {
                        'pointer-events': 'auto'
                    }),
                })
            ]
            if (props.secondary) {
                layers.push(h('canvas', {
                    attrs: Object.assign({
                        id: `${this.$props.tv_id}-${id}-canvas-x`
                    }, props.attrs),
                    ref: 'cursorCanvas',
                    // Deliberately NOT spreading props.style here: it may set
                    // an opaque backgroundColor on the primary canvas, which
                    // would hide it if reused on this transparent overlay.
                    style: {
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        'pointer-events': 'none',
                        background: 'transparent',
                    },
                }))
            }
            return h('div', {
                class: `tc-${id}`,
                on: {
                    mousemove: e => this.renderer && this.renderer.mousemove(e),
                    mouseleave: e => this.renderer && this.renderer.mouseout(e),
                },
                style: {
                    left: props.position.x + 'px',
                    top: props.position.y + 'px',
                    position: 'absolute',
                    'pointer-events': 'none',
                }
            }, layers.concat(props.hs || []))
        },
        redraw() {
            if (!this.renderer) return
            // Coalesce bursts of redraw() calls (range watch, cursor/mousemove
            // watch, shader watch, etc. can all fire within the same tick) into
            // a single paint per animation frame instead of repainting the
            // whole canvas synchronously for every trigger. This is purely a
            // scheduling change — renderer.update()/draw() internals are
            // untouched, so overlay output is identical, just coalesced.
            if (this._redrawScheduled) return
            this._redrawScheduled = true
            this._redrawFrame = requestAnimationFrame(() => {
                this._redrawScheduled = false
                this._redrawFrame = null
                if (this.renderer) this.renderer.update()
            })
        },
        redrawCursor() {
            if (!this.renderer) return
            // Lightweight path for cursor/crosshair-only changes: if the
            // renderer exposes a dedicated update_crosshair() (i.e. a
            // secondary canvas was set up), repaint only that instead of
            // the whole grid. Falls back to the full redraw() otherwise,
            // so this is safe to call unconditionally.
            if (!this.renderer.update_crosshair) return this.redraw()
            if (this._cursorRedrawScheduled) return
            this._cursorRedrawScheduled = true
            this._cursorRedrawFrame = requestAnimationFrame(() => {
                this._cursorRedrawScheduled = false
                this._cursorRedrawFrame = null
                if (this.renderer) this.renderer.update_crosshair()
            })
        }
    },
    beforeDestroy() {
        if (this._redrawFrame) {
            cancelAnimationFrame(this._redrawFrame)
            this._redrawFrame = null
        }
        if (this._cursorRedrawFrame) {
            cancelAnimationFrame(this._cursorRedrawFrame)
            this._cursorRedrawFrame = null
        }
    },
    watch: {
        width(val) {
            this._attrs.width = val
            this.setup()
        },
        height(val) {
            this._attrs.height = val
            this.setup()
        }
    }
}
